import { useCallback, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { getListTherapistNominations } from "../../api/therapistNomination";
import useDebounce from "../../hooks/useDebounce";
import { isNumeric } from "../../utils";

const toYearMonthLocal = (date) => {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

const useMonthlyTherapistNomination = () => {
  const [activeSortColumn, setActiveSortColumn] = useState(0);
  const [sortColumnDirection, setSortColumnDirection] = useState("desc");
  const [sortedData, setSortedData] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(10000);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [searchName, setSearchName] = useState("");

  // Debounce search name with 500ms delay
  const debouncedSearchName = useDebounce(searchName, 500);

  const {
    data: therapistNominationsData,
    refetch,
    isLoading,
  } = useQuery(
    ["therapistNominations", page, rowsPerPage, selectedMonth, debouncedSearchName],
    () =>
      getListTherapistNominations({
        page: page + 1,
        per_page: rowsPerPage,
        // IMPORTANT: use local year-month (avoid UTC shift around month boundaries)
        year_month: toYearMonthLocal(selectedMonth),
        name: debouncedSearchName,
      }),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (therapistNominationsData?.data && !isLoading) {
      setSortedData(therapistNominationsData.data.data.sort((a, b) => b.id - a.id));
      setTotal(therapistNominationsData.data.total || 0);
    }
  }, [therapistNominationsData, isLoading]);

  const handleSort = useCallback((columnIndex) => {
    const isSorted = activeSortColumn === columnIndex;
    const direction = isSorted
      ? sortColumnDirection === "asc"
        ? "desc"
        : "asc"
      : "desc";
    const sorted = [...sortedData].sort((a, b) => {
      let aValue = a[Object.keys(a)[columnIndex]];
      let bValue = b[Object.keys(b)[columnIndex]];

      // if aValue and bValue are numberic, sort parseInt
      if (isNumeric(aValue) && isNumeric(bValue)) {
        aValue = parseInt(aValue);
        bValue = parseInt(bValue);
      }

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setSortedData(sorted);
    setActiveSortColumn(columnIndex);
    setSortColumnDirection(direction);
  }, [activeSortColumn, sortColumnDirection, sortedData]);

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handlePreviousMonth = () => {
    const newDate = new Date(selectedMonth);
    newDate.setMonth(newDate.getMonth() - 1);
    setSelectedMonth(newDate);
    setPage(0);
  };

  const handleNextMonth = () => {
    const newDate = new Date(selectedMonth);
    newDate.setMonth(newDate.getMonth() + 1);
    setSelectedMonth(newDate);
    setPage(0);
  };

  const handleMonthChange = (newValue) => {
    // MUI DatePicker onChange gives Date | null
    if (!newValue) return;
    const next = new Date(newValue);
    if (Number.isNaN(next.getTime())) return;
    setSelectedMonth(next);
    setPage(0);
  };

  const handleSearchName = (event) => {
    setSearchName(event.target.value);
    setPage(0);
  };

  return {
    activeSortColumn,
    sortColumnDirection,
    sortedData,
    rowsPerPage,
    total,
    page,
    selectedMonth,
    searchName,
    handleRowsPerPageChange,
    handlePageChange,
    handleSort,
    handlePreviousMonth,
    handleNextMonth,
    handleMonthChange,
    handleSearchName,
    refetch,
    isLoading,
  };
};

export default useMonthlyTherapistNomination;
